import { useState, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput,
  TouchableOpacity, Modal, Alert,
} from 'react-native';
import { Student } from '@/lib/types';

type Protocol = 'pollock3' | 'pollock7' | 'none';

export interface AssessmentData {
  weight_kg: number;
  body_fat_pct?: number;
  recorded_at: string;
  measurements: Record<string, number | string>;
}

interface Props {
  visible: boolean;
  student: Student;
  onSave: (data: AssessmentData) => Promise<void>;
  onClose: () => void;
}

function calcAge(birthDate: string | null): number {
  if (!birthDate) return 30;
  const diff = Date.now() - new Date(birthDate).getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
}

function siri(D: number): number {
  return Math.min(50, Math.max(2, (495 / D) - 450));
}

function pollock3Male(chest: number, abdomen: number, thigh: number, age: number): number {
  const S = chest + abdomen + thigh;
  return siri(1.10938 - 0.0008267 * S + 0.0000016 * S * S - 0.0002574 * age);
}

function pollock3Female(triceps: number, suprailiac: number, thigh: number, age: number): number {
  const S = triceps + suprailiac + thigh;
  return siri(1.0994921 - 0.0009929 * S + 0.0000023 * S * S - 0.0001392 * age);
}

function pollock7(
  chest: number, midaxillary: number, triceps: number, subscapular: number,
  abdomen: number, suprailiac: number, thigh: number, age: number,
): number {
  const S = chest + midaxillary + triceps + subscapular + abdomen + suprailiac + thigh;
  return siri(1.112 - 0.00043499 * S + 0.00000055 * S * S - 0.00028826 * age);
}

function parseField(v: string): number | null {
  const n = parseFloat(v.replace(',', '.'));
  return isNaN(n) ? null : n;
}

export default function AssessmentForm({ visible, student, onSave, onClose }: Props) {
  const isFemale = student.sex === 'F' || student.sex === 'O';

  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [weight, setWeight] = useState('');
  const [protocol, setProtocol] = useState<Protocol>('none');
  const [manualFat, setManualFat] = useState('');
  const [waist, setWaist] = useState('');
  const [arm, setArm] = useState('');
  const [hip, setHip] = useState('');
  const [thighCirc, setThighCirc] = useState('');
  // Pollock 3 male
  const [p3mChest, setP3mChest] = useState('');
  const [p3mAbdomen, setP3mAbdomen] = useState('');
  const [p3mThigh, setP3mThigh] = useState('');
  // Pollock 3 female
  const [p3fTriceps, setP3fTriceps] = useState('');
  const [p3fSuprailiac, setP3fSuprailiac] = useState('');
  const [p3fThigh, setP3fThigh] = useState('');
  // Pollock 7
  const [p7Chest, setP7Chest] = useState('');
  const [p7Midaxillary, setP7Midaxillary] = useState('');
  const [p7Triceps, setP7Triceps] = useState('');
  const [p7Subscapular, setP7Subscapular] = useState('');
  const [p7Abdomen, setP7Abdomen] = useState('');
  const [p7Suprailiac, setP7Suprailiac] = useState('');
  const [p7Thigh, setP7Thigh] = useState('');
  const [saving, setSaving] = useState(false);

  const age = calcAge(student.birth_date);

  const calculatedFat = useMemo<number | null>(() => {
    if (protocol === 'pollock3') {
      if (isFemale) {
        const t = parseField(p3fTriceps), s = parseField(p3fSuprailiac), th = parseField(p3fThigh);
        if (t !== null && s !== null && th !== null) return pollock3Female(t, s, th, age);
      } else {
        const c = parseField(p3mChest), a = parseField(p3mAbdomen), th = parseField(p3mThigh);
        if (c !== null && a !== null && th !== null) return pollock3Male(c, a, th, age);
      }
    }
    if (protocol === 'pollock7') {
      const c = parseField(p7Chest), m = parseField(p7Midaxillary), t = parseField(p7Triceps),
            s = parseField(p7Subscapular), a = parseField(p7Abdomen),
            su = parseField(p7Suprailiac), th = parseField(p7Thigh);
      if (c !== null && m !== null && t !== null && s !== null && a !== null && su !== null && th !== null) {
        return pollock7(c, m, t, s, a, su, th, age);
      }
    }
    return null;
  }, [protocol, isFemale, age, p3mChest, p3mAbdomen, p3mThigh, p3fTriceps, p3fSuprailiac, p3fThigh,
      p7Chest, p7Midaxillary, p7Triceps, p7Subscapular, p7Abdomen, p7Suprailiac, p7Thigh]);

  function reset() {
    setDate(new Date().toISOString().split('T')[0]);
    setWeight(''); setProtocol('none'); setManualFat('');
    setWaist(''); setArm(''); setHip(''); setThighCirc('');
    setP3mChest(''); setP3mAbdomen(''); setP3mThigh('');
    setP3fTriceps(''); setP3fSuprailiac(''); setP3fThigh('');
    setP7Chest(''); setP7Midaxillary(''); setP7Triceps('');
    setP7Subscapular(''); setP7Abdomen(''); setP7Suprailiac(''); setP7Thigh('');
  }

  async function handleSave() {
    const w = parseField(weight);
    if (!w || w <= 0) { Alert.alert('Peso inválido', 'Informe o peso do aluno.'); return; }

    setSaving(true);
    try {
      const fatRaw = calculatedFat ?? parseField(manualFat);
      const fat = fatRaw !== null ? parseFloat(fatRaw.toFixed(1)) : undefined;

      const measurements: Record<string, number | string> = {};
      const wv = parseField(waist); if (wv) measurements.waist = wv;
      const av = parseField(arm); if (av) measurements.arm = av;
      const hv = parseField(hip); if (hv) measurements.hip = hv;
      const tv = parseField(thighCirc); if (tv) measurements.thigh = tv;

      if (protocol === 'pollock3') {
        measurements.skinfold_protocol = 'pollock3';
        if (isFemale) {
          const t = parseField(p3fTriceps); if (t) measurements.skinfold_triceps = t;
          const s = parseField(p3fSuprailiac); if (s) measurements.skinfold_suprailiac = s;
          const th = parseField(p3fThigh); if (th) measurements.skinfold_thigh = th;
        } else {
          const c = parseField(p3mChest); if (c) measurements.skinfold_chest = c;
          const a = parseField(p3mAbdomen); if (a) measurements.skinfold_abdomen = a;
          const th = parseField(p3mThigh); if (th) measurements.skinfold_thigh = th;
        }
      } else if (protocol === 'pollock7') {
        measurements.skinfold_protocol = 'pollock7';
        const c = parseField(p7Chest); if (c) measurements.skinfold_chest = c;
        const m = parseField(p7Midaxillary); if (m) measurements.skinfold_midaxillary = m;
        const t = parseField(p7Triceps); if (t) measurements.skinfold_triceps = t;
        const s = parseField(p7Subscapular); if (s) measurements.skinfold_subscapular = s;
        const a = parseField(p7Abdomen); if (a) measurements.skinfold_abdomen = a;
        const su = parseField(p7Suprailiac); if (su) measurements.skinfold_suprailiac = su;
        const th = parseField(p7Thigh); if (th) measurements.skinfold_thigh = th;
      }

      await onSave({
        weight_kg: w,
        body_fat_pct: fat,
        recorded_at: new Date(date + 'T12:00:00').toISOString(),
        measurements,
      });
      reset();
      onClose();
    } catch {
      Alert.alert('Erro', 'Não foi possível salvar a avaliação.');
    } finally {
      setSaving(false);
    }
  }

  const PROTOCOLS: { key: Protocol; label: string }[] = [
    { key: 'pollock3', label: 'Pollock 3' },
    { key: 'pollock7', label: 'Pollock 7' },
    { key: 'none', label: 'Sem dobras' },
  ];

  const Field = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <View style={s.fieldRow}>
      <Text style={s.fieldLabel}>{label}</Text>
      <TextInput
        style={s.fieldInput}
        value={value}
        onChangeText={onChange}
        keyboardType="decimal-pad"
        placeholder="—"
        placeholderTextColor="#ccc"
      />
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={s.sheet}>
          <View style={s.header}>
            <Text style={s.title}>Nova Avaliação</Text>
            <TouchableOpacity onPress={onClose}><Text style={s.close}>✕</Text></TouchableOpacity>
          </View>
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>

            {/* Date */}
            <Text style={s.sectionLabel}>Data da avaliação</Text>
            <TextInput
              style={s.dateInput}
              value={date}
              onChangeText={setDate}
              placeholder="AAAA-MM-DD"
              keyboardType="numeric"
            />

            {/* Weight */}
            <Text style={s.sectionLabel}>Peso (kg)</Text>
            <TextInput
              style={s.dateInput}
              value={weight}
              onChangeText={setWeight}
              keyboardType="decimal-pad"
              placeholder="0,0"
            />

            {/* Protocol */}
            <Text style={s.sectionLabel}>Dobras cutâneas</Text>
            <View style={s.protocolRow}>
              {PROTOCOLS.map(p => (
                <TouchableOpacity
                  key={p.key}
                  style={[s.protocolBtn, protocol === p.key && s.protocolBtnActive]}
                  onPress={() => setProtocol(p.key)}
                >
                  <Text style={[s.protocolBtnText, protocol === p.key && s.protocolBtnTextActive]}>
                    {p.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Skinfold fields */}
            {protocol === 'pollock3' && !isFemale && (
              <View style={s.skinfoldGroup}>
                <Field label="Peitoral (mm)" value={p3mChest} onChange={setP3mChest} />
                <Field label="Abdômen (mm)" value={p3mAbdomen} onChange={setP3mAbdomen} />
                <Field label="Coxa (mm)" value={p3mThigh} onChange={setP3mThigh} />
              </View>
            )}
            {protocol === 'pollock3' && isFemale && (
              <View style={s.skinfoldGroup}>
                <Field label="Tríceps (mm)" value={p3fTriceps} onChange={setP3fTriceps} />
                <Field label="Suprailíaca (mm)" value={p3fSuprailiac} onChange={setP3fSuprailiac} />
                <Field label="Coxa (mm)" value={p3fThigh} onChange={setP3fThigh} />
              </View>
            )}
            {protocol === 'pollock7' && (
              <View style={s.skinfoldGroup}>
                <Field label="Peitoral (mm)" value={p7Chest} onChange={setP7Chest} />
                <Field label="Axilar médio (mm)" value={p7Midaxillary} onChange={setP7Midaxillary} />
                <Field label="Tríceps (mm)" value={p7Triceps} onChange={setP7Triceps} />
                <Field label="Subescapular (mm)" value={p7Subscapular} onChange={setP7Subscapular} />
                <Field label="Abdômen (mm)" value={p7Abdomen} onChange={setP7Abdomen} />
                <Field label="Suprailíaca (mm)" value={p7Suprailiac} onChange={setP7Suprailiac} />
                <Field label="Coxa (mm)" value={p7Thigh} onChange={setP7Thigh} />
              </View>
            )}

            {/* Calculated fat preview */}
            {calculatedFat !== null && (
              <View style={s.fatPreview}>
                <Text style={s.fatPreviewText}>% Gordura calculada: {calculatedFat.toFixed(1)}%</Text>
              </View>
            )}

            {/* Manual fat (when no protocol) */}
            {protocol === 'none' && (
              <>
                <Text style={s.sectionLabel}>% Gordura (manual / bioimpedância)</Text>
                <TextInput
                  style={s.dateInput}
                  value={manualFat}
                  onChangeText={setManualFat}
                  keyboardType="decimal-pad"
                  placeholder="Opcional"
                />
              </>
            )}

            {/* Circumferences */}
            <Text style={s.sectionLabel}>Circunferências (cm)</Text>
            <View style={s.skinfoldGroup}>
              <Field label="Cintura" value={waist} onChange={setWaist} />
              <Field label="Braço" value={arm} onChange={setArm} />
              <Field label="Quadril" value={hip} onChange={setHip} />
              <Field label="Coxa" value={thighCirc} onChange={setThighCirc} />
            </View>

            {/* Actions */}
            <View style={s.actions}>
              <TouchableOpacity style={s.cancelBtn} onPress={onClose}>
                <Text style={s.cancelBtnText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.saveBtn, saving && { opacity: 0.5 }]}
                onPress={handleSave}
                disabled={saving}
              >
                <Text style={s.saveBtnText}>{saving ? 'Salvando...' : 'Salvar'}</Text>
              </TouchableOpacity>
            </View>

          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '90%' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 18, fontWeight: '700', color: '#1A1A1A' },
  close: { fontSize: 18, color: '#888', padding: 4 },
  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 14, marginBottom: 6 },
  dateInput: { borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 10, fontSize: 14, color: '#1A1A1A' },
  protocolRow: { flexDirection: 'row', gap: 8 },
  protocolBtn: { flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1.5, borderColor: '#ddd', alignItems: 'center' },
  protocolBtnActive: { borderColor: '#C8102E', backgroundColor: '#FFF0F2' },
  protocolBtnText: { fontSize: 12, fontWeight: '600', color: '#888' },
  protocolBtnTextActive: { color: '#C8102E' },
  skinfoldGroup: { backgroundColor: '#f9f9f9', borderRadius: 10, padding: 10, gap: 2, marginTop: 6 },
  fieldRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  fieldLabel: { fontSize: 13, color: '#555', flex: 1 },
  fieldInput: { width: 80, borderWidth: 1, borderColor: '#ddd', borderRadius: 6, padding: 6, fontSize: 13, color: '#1A1A1A', textAlign: 'right' },
  fatPreview: { backgroundColor: '#E8F5E9', borderRadius: 8, padding: 10, marginTop: 8, alignItems: 'center' },
  fatPreviewText: { fontSize: 14, fontWeight: '700', color: '#2E7D32' },
  actions: { flexDirection: 'row', gap: 10, marginTop: 20 },
  cancelBtn: { flex: 1, padding: 12, borderRadius: 8, borderWidth: 1, borderColor: '#e0e0e0', alignItems: 'center' },
  cancelBtnText: { fontSize: 14, fontWeight: '600', color: '#888' },
  saveBtn: { flex: 1, padding: 12, borderRadius: 8, backgroundColor: '#C8102E', alignItems: 'center' },
  saveBtnText: { fontSize: 14, fontWeight: '700', color: '#fff' },
});
